import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { PaymentFormValues } from 'src/pages/payment-msi/PaymentMsiV2/PaymentMsiV2';
import { AdditionalInsuredActionButton } from 'src/pages/payment-msi/PaymentMsiV2/components/AdditionalInsuredActionButton';
import { DeleteAdditionalInsuredButton } from 'src/pages/payment-msi/PaymentMsiV2/components/DeleteAdditionalInsuredButton';
import { PaymentTextField } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentTextField';
import { ErrorIcon } from 'src/pages/payment-msi/PaymentMsiV2/icons/ErrorIcon';
import { isAdditinalInsuredSwitchEnabled } from 'src/pages/payment-msi/PaymentMsiV2/utils';
import { AddIcon } from '../icons/AddIcon';
import { FormRow } from './FormRow';
import { PaymentSection, PaymentSectionHeader } from './PaymentSection';

const additionalInsuredStyles = {
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '24px',
  },
  formButtonWrapper: {
    marginTop: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};

export function AdditionalInsuredSection() {
  const {
    control,
    formState: { errors, defaultValues },
  } = useFormContext<PaymentFormValues>();

  const [isSwitchActive, setIsSwitchActive] = useState(
    isAdditinalInsuredSwitchEnabled(defaultValues?.additionalInsured),
  );

  const { fields, remove, append } = useFieldArray({
    control: control,
    name: 'additionalInsured',
  });

  function handleSwitchChange() {
    setIsSwitchActive((prevState) => !prevState);
  }

  function onAddInsured() {
    append({
      email: '',
      firstName: '',
      lastName: '',
    });
  }

  function onDeleteInsured(index: number) {
    remove(index);
  }

  useEffect(() => {
    const hasAdditionalInsuredErrors =
      (errors.additionalInsured?.length ?? 0) > 0;

    if (hasAdditionalInsuredErrors) {
      setIsSwitchActive(true);
    }
  }, [errors]);

  return (
    <PaymentSection>
      <PaymentSectionHeader
        hasSwitch={true}
        switchOpen={isSwitchActive}
        onSwitchChange={handleSwitchChange}
        sectionTitle={'Add additional insured'}
        desktopTooltipText={
          'Additional insured are people that you live with and will be covered by your policy. Usually a spouse, partner or roommate.'
        }
        mobileDescription={'I want to add information on additional insured'}
      />
      <Collapse in={isSwitchActive} timeout={'auto'}>
        <Box marginTop={'40px'}>
          {fields.map((additionalInsuredField, index) => {
            const isFirstField = index === 0;
            const isLastField = index === fields.length - 1;
            const hasBottomMargin = !isLastField && fields.length > 1;
            const shouldRenderAddButton = isLastField || fields.length === 1;
            const shouldRenderDeleteButton = fields.length > 1 && !isFirstField;

            return (
              <Box
                key={additionalInsuredField.id}
                marginBottom={hasBottomMargin ? '16px' : '0'}
              >
                <Box sx={additionalInsuredStyles.formWrapper}>
                  <FormRow>
                    <PaymentTextField
                      name={`additionalInsured.${index}.firstName`}
                      label={'First Name'}
                      required={true}
                      error={Boolean(
                        errors.additionalInsured?.[index]?.firstName,
                      )}
                      helperText={
                        errors.additionalInsured?.[index]?.firstName?.message
                      }
                    />
                    <PaymentTextField
                      name={`additionalInsured.${index}.lastName`}
                      label={'Last Name'}
                      required={true}
                      error={Boolean(
                        errors.additionalInsured?.[index]?.lastName,
                      )}
                      helperText={
                        errors.additionalInsured?.[index]?.lastName?.message
                      }
                    />
                  </FormRow>
                  <PaymentTextField
                    name={`additionalInsured.${index}.email`}
                    label={'Email Address'}
                    required={true}
                    type={'email'}
                    error={Boolean(errors.additionalInsured?.[index]?.email)}
                    helperText={
                      errors.additionalInsured?.[index]?.email?.message
                    }
                  />
                </Box>
                {shouldRenderAddButton || shouldRenderDeleteButton ? (
                  <Box sx={additionalInsuredStyles.formButtonWrapper}>
                    {shouldRenderAddButton ? (
                      fields.length < 3 ? (
                        <AdditionalInsuredActionButton
                          icon={<AddIcon />}
                          onClick={onAddInsured}
                          variant="add"
                        >
                          Add
                        </AdditionalInsuredActionButton>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <ErrorIcon />
                          <Typography color="error" variant="body1">
                            You can’t add more than 3 additional insured
                          </Typography>
                        </Box>
                      )
                    ) : (
                      <Box />
                    )}
                    {shouldRenderDeleteButton ? (
                      <DeleteAdditionalInsuredButton
                        index={index}
                        onDelete={onDeleteInsured}
                      />
                    ) : null}
                  </Box>
                ) : (
                  <Box marginTop={'8px'} height={'42px'} />
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </PaymentSection>
  );
}
